/**
 * One Bluetooth chooser for both headbands (Sprint 1.9.8 S4). The person picks
 * a device once; its family is read from the service the device exposes, and
 * the page hands that same `BluetoothDevice` to the family's driver, so there
 * is no second chooser and no lost user gesture.
 */

import { SERVICE_UUID as CLASSIC_SERVICE_UUID, type BluetoothLike } from './brainbitClassic'
import { PRO_SERVICE_UUID } from './brainbitPro'
import { HeadbandError, webBluetoothAvailable } from './types'

const DEVICE_INFORMATION = 0x180a

export interface ChosenHeadband {
  device: BluetoothDevice
  family: 'brainbit' | 'brainbit_pro'
}

/** Must be called from a user gesture. */
export async function chooseHeadband(bluetooth: BluetoothLike | null = webBluetoothAvailable() ? navigator.bluetooth : null): Promise<ChosenHeadband> {
  if (!bluetooth) throw new HeadbandError('unsupported', 'Web Bluetooth is not available in this browser.')
  let device: BluetoothDevice
  try {
    device = await bluetooth.requestDevice({
      filters: [{ namePrefix: 'BrainBit' }, { namePrefix: 'BB Pro' }, { services: [CLASSIC_SERVICE_UUID] }, { services: [PRO_SERVICE_UUID] }],
      optionalServices: [CLASSIC_SERVICE_UUID, PRO_SERVICE_UUID, DEVICE_INFORMATION],
    })
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') throw new HeadbandError('cancelled', 'No headband was chosen.')
    throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'Could not open the Bluetooth chooser.')
  }
  try {
    const server = await device.gatt!.connect()
    const pro = await server.getPrimaryService(PRO_SERVICE_UUID).then(
      () => true,
      () => false,
    )
    return { device, family: pro ? 'brainbit_pro' : 'brainbit' }
  } catch (err) {
    throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'The headband did not answer.')
  }
}

/** A `BluetoothLike` whose chooser answers with a device already chosen (the classic driver asks for one). */
export function alreadyChosen(device: BluetoothDevice): BluetoothLike {
  return { requestDevice: async () => device }
}
